import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, ExternalLink, Plug, TestTube, Trash2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { formsApi } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Help links for each integration so users don't have to hunt for where
// to obtain the API key / IDs. Order matches the provider slug list.
const SETUP_LINKS = {
  trello:     { href: 'https://trello.com/power-ups/admin', label: 'Get API key + token' },
  zendesk:    { href: 'https://support.zendesk.com/hc/en-us/articles/4408889192858', label: 'Generate API token' },
  mixpanel:   { href: 'https://mixpanel.com/settings/project', label: 'Find project token' },
  segment:    { href: 'https://segment.com/docs/connections/sources/catalog/libraries/server/http-api/', label: 'Create HTTP API source' },
  coda:       { href: 'https://coda.io/account', label: 'Create API token' },
  smartsheet: { href: 'https://help.smartsheet.com/articles/2482389-generate-api-access-token', label: 'Generate access token' },
  smartsuite: { href: 'https://app.smartsuite.com/account/profile/api-keys', label: 'Manage API keys' },
  stackby:    { href: 'https://help.stackby.com/article/85-where-can-i-find-the-api-keys', label: 'Find API key' },
  close:      { href: 'https://app.close.com/settings/developer/api-keys/', label: 'API keys' },
  apollo:     { href: 'https://app.apollo.io/#/settings/integrations/api', label: 'API key & docs' },
  flodesk:    { href: 'https://app.flodesk.com/account/preferences/api', label: 'Generate API key' },
  drip:       { href: 'https://www.getdrip.com/user/edit', label: 'User & API tokens' },
  ontraport:  { href: 'https://app.ontraport.com/#!/setting/profile', label: 'Administration → API Keys' },
  omnisend:   { href: 'https://app.omnisend.com/#/my-account/integrations/api-keys', label: 'API keys' },
  copper:     { href: 'https://app.copper.com/companies/settings/integrations/api_keys', label: 'API keys' },
  folk:       { href: 'https://app.folk.app/settings/api', label: 'Generate API key' },
  ghost:      { href: 'https://ghost.org/docs/admin-api/', label: 'Ghost Admin API docs' },
  circle:     { href: 'https://app.circle.so/settings/api', label: 'Generate API token' },
  attio:        { href: 'https://app.attio.com/settings/developers', label: 'Generate API key' },
  followupboss: { href: 'https://app.followupboss.com/2/api', label: 'Create API key' },
  lacrm:        { href: 'https://account.lessannoyingcrm.com/app/Settings/Api', label: 'Find user code + API token' },
  nutshell:     { href: 'https://app.nutshell.com/setup/api-keys', label: 'Create API key' },
  asana:        { href: 'https://app.asana.com/0/my-apps', label: 'Generate personal access token' },
  clickup:      { href: 'https://app.clickup.com/settings/apps', label: 'Generate API token (pk_…)' },
  gohighlevel:  { href: 'https://app.gohighlevel.com/?page=settings&tab=api', label: 'Location API key (v1)' },
  ifttt:        { href: 'https://ifttt.com/maker_webhooks', label: 'Find your Webhooks (Maker) key' },
};

const SimpleIntegration = () => {
  const { provider } = useParams();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [activeFormId, setActiveFormId] = useState('');
  const [config, setConfig] = useState({});
  const [enabled, setEnabled] = useState(false);
  const [savedState, setSavedState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const token = localStorage.getItem('ff_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch schema + the user's forms.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [provRes, formsArr] = await Promise.all([
          fetch(`${API}/api/simple-integrations/providers`).then((r) => r.json()),
          formsApi.getAll().catch(() => []),
        ]);
        if (cancelled) return;
        const found = (provRes.providers || []).find((p) => p.slug === provider);
        if (!found) {
          toast.error('Unknown integration');
          navigate('/integrations');
          return;
        }
        setSchema(found);
        const arr = Array.isArray(formsArr) ? formsArr : (formsArr?.forms || []);
        setForms(arr);
        if (arr.length > 0) setActiveFormId(arr[0].id);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [provider, navigate]);

  // Whenever the picked form changes, reload its saved config (masked).
  useEffect(() => {
    if (!activeFormId || !provider) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/simple-integrations/${provider}/forms/${activeFormId}`, { headers });
        const data = await res.json();
        if (cancelled) return;
        setConfig(data.config || {});
        setEnabled(!!data.enabled);
        setSavedState(data);
      } catch (e) {
        if (!cancelled) {
          setConfig({});
          setEnabled(false);
          setSavedState(null);
        }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFormId, provider]);

  const handleField = (name, value) => setConfig((c) => ({ ...c, [name]: value }));

  const onSave = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/simple-integrations/${provider}/forms/${activeFormId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ enabled, config }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Save failed');
      setConfig(data.config || {});
      setSavedState(data);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/simple-integrations/${provider}/forms/${activeFormId}/test`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Test failed');
      toast.success(data.message || 'Connection looks good');
    } catch (e) {
      toast.error(e.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Disconnect ${schema?.label} from this form?`)) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/simple-integrations/${provider}/forms/${activeFormId}`, { method: 'DELETE', headers });
      setConfig({});
      setEnabled(false);
      setSavedState(null);
      toast.success('Disconnected');
    } catch {
      toast.error('Could not disconnect');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !schema) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const setupLink = SETUP_LINKS[provider];

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/integrations" className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="back-to-integrations">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
            style={{ backgroundColor: schema.color }}
          >
            <Plug className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">{schema.label}</h1>
            <p className="text-xs text-zinc-500">Per-form integration · pushes every submission</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Form picker */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Form</Label>
            <Select value={activeFormId} onValueChange={setActiveFormId}>
              <SelectTrigger data-testid="simple-int-form-select" className="w-full">
                <SelectValue placeholder="Pick a form…" />
              </SelectTrigger>
              <SelectContent>
                {forms.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-zinc-500">No forms yet.</div>
                ) : forms.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.title || '(untitled)'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {forms.length === 0 && (
              <p className="text-xs text-zinc-500">
                Create a form first, then come back here to connect it.
              </p>
            )}
          </CardContent>
        </Card>

        {activeFormId && (
          <>
            <Card>
              <CardContent className="p-5 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Connection</h3>
                    <p className="text-xs text-zinc-500">
                      Turn this on and every new submission will be pushed to {schema.label}.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {savedState?.last_synced_at && enabled && (
                      <Badge className="bg-green-100 text-green-700 text-xs">Live</Badge>
                    )}
                    <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="simple-int-enabled" />
                  </div>
                </div>

                {setupLink && (
                  <div className="text-xs text-zinc-500">
                    Need your credentials?{' '}
                    <a
                      href={setupLink.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 text-zinc-900 underline hover:text-[#FE6A6B]"
                      data-testid="simple-int-help-link"
                    >
                      {setupLink.label} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                {/* Credentials/config fields */}
                <div className="space-y-3">
                  {schema.fields.map((f) => (
                    <div key={f.name} className="space-y-1">
                      <Label htmlFor={`field-${f.name}`} className="text-xs text-zinc-700">
                        {f.label} {f.required && <span className="text-zinc-400">·</span>} {f.required && <span className="text-[10px] uppercase tracking-wider text-zinc-400">required</span>}
                      </Label>
                      {f.type === 'textarea' ? (
                        <Textarea
                          id={`field-${f.name}`}
                          rows={4}
                          placeholder={f.placeholder || ''}
                          value={config[f.name] || ''}
                          onChange={(e) => handleField(f.name, e.target.value)}
                          className="font-mono text-xs"
                          data-testid={`simple-int-field-${f.name}`}
                        />
                      ) : (
                        <Input
                          id={`field-${f.name}`}
                          type={f.type === 'password' ? 'password' : f.type === 'url' ? 'url' : 'text'}
                          placeholder={f.placeholder || ''}
                          value={config[f.name] || ''}
                          onChange={(e) => handleField(f.name, e.target.value)}
                          data-testid={`simple-int-field-${f.name}`}
                        />
                      )}
                      {f.help && <p className="text-[11px] text-zinc-500">{f.help}</p>}
                    </div>
                  ))}
                </div>

                {savedState?.last_error && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="simple-int-last-error">
                    <strong>Last error:</strong> {savedState.last_error}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <Button onClick={onSave} disabled={busy} data-testid="simple-int-save">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                  <Button variant="outline" onClick={onTest} disabled={testing || busy} data-testid="simple-int-test">
                    {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                    Test connection
                  </Button>
                  <div className="flex-1" />
                  {savedState?.config && Object.keys(savedState.config).length > 0 && (
                    <Button variant="ghost" onClick={onDelete} disabled={busy}
                            className="text-red-600 hover:bg-red-50" data-testid="simple-int-delete">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default SimpleIntegration;
